import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
  AlertCircle,
  Calendar,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  ChevronRight
} from 'lucide-react';
import { dashboardFinanceiroService, formatCurrency, formatDateTime } from '@/services';
import { useNavigate } from 'react-router-dom';

const DashboardFinanceiro = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Estados
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [fluxoCaixa, setFluxoCaixa] = useState([]);
  const [receitasPorCategoria, setReceitasPorCategoria] = useState([]);
  const [despesasPorCategoria, setDespesasPorCategoria] = useState([]);
  const [alertas, setAlertas] = useState(null);
  const [atividades, setAtividades] = useState([]);
  const [tipoFluxo, setTipoFluxo] = useState('mensal');

  // Cores para gráficos
  const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#84cc16'];

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      // Carregar todos os dados em paralelo
      const [
        overviewData,
        fluxoData,
        receitasCatData,
        despesasCatData,
        alertasData,
        atividadesData
      ] = await Promise.all([
        dashboardFinanceiroService.getOverview(),
        dashboardFinanceiroService.getFluxoCaixa('mensal'),
        dashboardFinanceiroService.getReceitasPorCategoria(),
        dashboardFinanceiroService.getDespesasPorCategoria(),
        dashboardFinanceiroService.getAlertas(),
        dashboardFinanceiroService.getAtividadesRecentes(10)
      ]);

      setOverview(overviewData);
      setFluxoCaixa(fluxoData.fluxo);
      setReceitasPorCategoria(receitasCatData.categorias);
      setDespesasPorCategoria(despesasCatData.categorias);
      setAlertas(alertasData);
      setAtividades(atividadesData.atividades);

    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeTipoFluxo = async (tipo) => {
    setTipoFluxo(tipo);
    try {
      const fluxoData = await dashboardFinanceiroService.getFluxoCaixa(tipo);
      setFluxoCaixa(fluxoData.fluxo);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-500">Carregando dashboard financeiro...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Financeiro</h1>
          <p className="text-gray-500 mt-1">
            Visão geral da saúde financeira da empresa • {overview?.periodo?.descricao}
          </p>
        </div>
        <Button onClick={loadDashboard} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Alertas Críticos */}
      {alertas && alertas.total_alertas > 0 && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>{alertas.total_alertas} alertas</strong> requerem atenção: 
            {alertas.receitas.atrasadas > 0 && ` ${alertas.receitas.atrasadas} receitas atrasadas`}
            {alertas.despesas.atrasadas > 0 && ` • ${alertas.despesas.atrasadas} despesas atrasadas`}
            {alertas.contratos.renovando_90dias > 0 && ` • ${alertas.contratos.renovando_90dias} contratos renovando`}
          </AlertDescription>
        </Alert>
      )}

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Receitas */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/portal/financeiro/receitas')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Receitas do Mês
            </CardTitle>
            <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(overview?.kpis?.receitas?.recebidas || 0)}
            </div>
            <div className="flex items-center mt-2">
              {overview?.kpis?.receitas?.variacao_percentual >= 0 ? (
                <ArrowUpRight className="h-4 w-4 text-green-600 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                overview?.kpis?.receitas?.variacao_percentual >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {Math.abs(overview?.kpis?.receitas?.variacao_percentual || 0).toFixed(1)}%
              </span>
              <span className="text-sm text-gray-500 ml-1">vs mês anterior</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              A receber: {formatCurrency(overview?.kpis?.receitas?.a_receber || 0)}
            </p>
          </CardContent>
        </Card>

        {/* Despesas */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/portal/financeiro/despesas')}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Despesas do Mês
            </CardTitle>
            <div className="h-8 w-8 bg-red-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="h-4 w-4 text-red-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(overview?.kpis?.despesas?.pagas || 0)}
            </div>
            <div className="flex items-center mt-2">
              {overview?.kpis?.despesas?.variacao_percentual >= 0 ? (
                <ArrowUpRight className="h-4 w-4 text-red-600 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-green-600 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                overview?.kpis?.despesas?.variacao_percentual >= 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {Math.abs(overview?.kpis?.despesas?.variacao_percentual || 0).toFixed(1)}%
              </span>
              <span className="text-sm text-gray-500 ml-1">vs mês anterior</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              A pagar: {formatCurrency(overview?.kpis?.despesas?.a_pagar || 0)}
            </p>
          </CardContent>
        </Card>

        {/* Saldo */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Saldo do Mês
            </CardTitle>
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
              (overview?.kpis?.saldo?.mes_atual || 0) >= 0 ? 'bg-purple-100' : 'bg-orange-100'
            }`}>
              <DollarSign className={`h-4 w-4 ${
                (overview?.kpis?.saldo?.mes_atual || 0) >= 0 ? 'text-purple-600' : 'text-orange-600'
              }`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              (overview?.kpis?.saldo?.mes_atual || 0) >= 0 ? 'text-gray-900' : 'text-orange-600'
            }`}>
              {formatCurrency(overview?.kpis?.saldo?.mes_atual || 0)}
            </div>
            <div className="flex items-center mt-2">
              {overview?.kpis?.saldo?.variacao_percentual >= 0 ? (
                <ArrowUpRight className="h-4 w-4 text-green-600 mr-1" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                overview?.kpis?.saldo?.variacao_percentual >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {Math.abs(overview?.kpis?.saldo?.variacao_percentual || 0).toFixed(1)}%
              </span>
              <span className="text-sm text-gray-500 ml-1">vs mês anterior</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Receitas - Despesas
            </p>
          </CardContent>
        </Card>

        {/* Margem de Lucro */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Margem de Lucro
            </CardTitle>
            <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Activity className="h-4 w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {overview?.kpis?.margem_lucro?.toFixed(1) || 0}%
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>Meta: 70%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    (overview?.kpis?.margem_lucro || 0) >= 70 ? 'bg-green-500' : 
                    (overview?.kpis?.margem_lucro || 0) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(overview?.kpis?.margem_lucro || 0, 100)}%` }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {(overview?.kpis?.margem_lucro || 0) >= 70 ? 'Excelente!' : 
               (overview?.kpis?.margem_lucro || 0) >= 50 ? 'Bom' : 'Atenção necessária'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos e Detalhes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fluxo de Caixa */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Fluxo de Caixa</CardTitle>
                <CardDescription>Receitas e despesas ao longo do tempo</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={tipoFluxo === 'mensal' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleChangeTipoFluxo('mensal')}
                >
                  Mensal
                </Button>
                <Button
                  variant={tipoFluxo === 'anual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleChangeTipoFluxo('anual')}
                >
                  Anual
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={fluxoCaixa}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodo" />
                <YAxis />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: '#000' }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="receitas"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Receitas"
                />
                <Line
                  type="monotone"
                  dataKey="despesas"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Despesas"
                />
                <Line
                  type="monotone"
                  dataKey="saldo"
                  stroke="#6366f1"
                  strokeWidth={2}
                  name="Saldo"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Alertas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              Alertas
            </CardTitle>
            <CardDescription>Itens que requerem atenção</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {alertas?.receitas?.vencendo_7dias > 0 && (
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100"
                   onClick={() => navigate('/portal/financeiro/receitas')}>
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-yellow-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {alertas.receitas.vencendo_7dias} receitas vencendo
                    </p>
                    <p className="text-xs text-gray-500">Próximos 7 dias</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            )}

            {alertas?.receitas?.atrasadas > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100"
                   onClick={() => navigate('/portal/financeiro/receitas')}>
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {alertas.receitas.atrasadas} receitas atrasadas
                    </p>
                    <p className="text-xs text-gray-500">Requerem cobrança</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            )}

            {alertas?.despesas?.vencendo_7dias > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100"
                   onClick={() => navigate('/portal/financeiro/despesas')}>
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {alertas.despesas.vencendo_7dias} despesas vencendo
                    </p>
                    <p className="text-xs text-gray-500">Próximos 7 dias</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            )}

            {alertas?.contratos?.renovando_90dias > 0 && (
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100"
                   onClick={() => navigate('/portal/financeiro/contratos')}>
                <div className="flex items-center gap-3">
                  <CreditCard className="h-4 w-4 text-purple-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {alertas.contratos.renovando_90dias} contratos renovando
                    </p>
                    <p className="text-xs text-gray-500">Próximos 90 dias</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            )}

            {(!alertas || alertas.total_alertas === 0) && (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 mx-auto mb-2 text-green-600" />
                <p className="text-sm text-gray-500">Nenhum alerta no momento</p>
                <p className="text-xs text-gray-400 mt-1">Tudo está em dia! 🎉</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Distribuições */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Receitas por Categoria */}
        <Card>
          <CardHeader>
            <CardTitle>Receitas por Tipo</CardTitle>
            <CardDescription>Distribuição das receitas recebidas</CardDescription>
          </CardHeader>
          <CardContent>
            {receitasPorCategoria && receitasPorCategoria.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={receitasPorCategoria}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ _id, percentual }) => `${_id}: ${percentual}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="total"
                    >
                      {receitasPorCategoria.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {receitasPorCategoria.map((cat, index) => (
                    <div key={cat.id || cat._id || `receita-cat-${index}`} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-gray-700">{cat.id || cat._id}</span>
                      </div>
                      <span className="font-medium">{formatCurrency(cat.total)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Nenhuma receita registrada no período
              </div>
            )}
          </CardContent>
        </Card>

        {/* Despesas por Categoria */}
        <Card>
          <CardHeader>
            <CardTitle>Despesas por Categoria</CardTitle>
            <CardDescription>Distribuição das despesas pagas</CardDescription>
          </CardHeader>
          <CardContent>
            {despesasPorCategoria && despesasPorCategoria.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={despesasPorCategoria}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="_id" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="total" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {despesasPorCategoria.slice(0, 5).map((cat, index) => (
                    <div key={cat.id || cat._id || `despesa-cat-${index}`} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{cat.id || cat._id || 'Sem categoria'}</span>
                      <span className="font-medium">{formatCurrency(cat.total)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Nenhuma despesa registrada no período
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Atividades Recentes e Contratos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Atividades Recentes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Atividades Recentes</CardTitle>
            <CardDescription>Últimas movimentações financeiras</CardDescription>
          </CardHeader>
          <CardContent>
            {atividades && atividades.length > 0 ? (
              <div className="space-y-4">
                {atividades.map((atividade) => (
                  <div key={atividade.id} className="flex items-start gap-4 pb-4 border-b last:border-0">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      atividade.tipo === 'receita' ? 'bg-green-100' :
                      atividade.tipo === 'despesa' ? 'bg-red-100' :
                      'bg-purple-100'
                    }`}>
                      {atividade.tipo === 'receita' ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : atividade.tipo === 'despesa' ? (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      ) : (
                        <CreditCard className="h-4 w-4 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {atividade.descricao}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {atividade.usuario_nome} • {formatDateTime(atividade.data)}
                      </p>
                    </div>
                    <Badge variant="secondary" className="flex-shrink-0">
                      {atividade.acao}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Nenhuma atividade recente
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resumo de Contratos */}
        <Card>
          <CardHeader>
            <CardTitle>Contratos Ativos</CardTitle>
            <CardDescription>Resumo da base de contratos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div>
                <p className="text-2xl font-bold text-purple-600">
                  {overview?.contratos?.ativos || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1">Contratos ativos</p>
              </div>
              <CreditCard className="h-8 w-8 text-purple-600" />
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Receita mensal recorrente</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(overview?.contratos?.valor_mensal_total || 0)}
              </p>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-gray-600 mb-1">Renovando em breve</p>
              <p className="text-xl font-bold text-yellow-600">
                {overview?.contratos?.proximos_renovacao || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">Próximos 90 dias</p>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/portal/financeiro/contratos')}
            >
              Ver Todos os Contratos
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Inadimplência */}
      {overview?.inadimplencia && overview.inadimplencia.quantidade > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-900">⚠️ Atenção: Inadimplência Detectada</CardTitle>
            <CardDescription className="text-red-700">
              Há receitas em atraso que requerem ação imediata
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total em Atraso</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {formatCurrency(overview.inadimplencia.valor_total)}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-gray-600">Quantidade de Faturas</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {overview.inadimplencia.quantidade}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-sm text-gray-600">Taxa de Inadimplência</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {overview.inadimplencia.taxa_percentual.toFixed(1)}%
                </p>
              </div>
            </div>
            <Button
              className="mt-4 bg-red-600 hover:bg-red-700"
              onClick={() => navigate('/portal/financeiro/receitas')}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Gerenciar Receitas Atrasadas
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardFinanceiro;
